import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Alert, Box } from "@mui/material";
import "./App.css";
import LoginPage from "./LoginPage";
import ProjectList from "modules/contracts/components/projects/ProjectList";
import UserList from "modules/systems/components/users/UserList";
import ProtectedRoute from "components/ProtectedRoute";
import { AuthProvider, useAuth } from "context/AuthContext";
import { AuthorizationProvider } from "auth/AuthorizationContext";
import { PageTitleProvider } from "context/PageTitleContext";
import { ConfirmProvider } from "components/common/dialogs";
import RegisterPage from "./RegisterPage";
import AppLayout from "components/layouts/AppLayout";
import PersonnelList from "modules/systems/components/personnel/PersonnelList";
import ContractDetail from "modules/contracts/components/projects/ContractDetail";
import PermissionManagement from "modules/systems/pages/permission/PermissionManagement";
import RolePermissionManagement from "modules/systems/pages/permission/RolePermissionManagement";
import UserRoleManagement from "modules/systems/pages/permission/UserRoleManagement";
import PermissionOverview from "modules/systems/pages/permission/PermissionOverview";
import UserPermissionManagement from "modules/systems/pages/permission/UserPermissionManagement";
import ApplicationManagement from "modules/systems/pages/permission/ApplicationManagement";
import PortalPage from "pages/PortalPage";
import ApplicationDevelopmentPage from "pages/ApplicationDevelopmentPage";
import Unauthorized from "pages/Unauthorized";
import PermissionGuard from "components/PermissionGuard";
import ScopedPermissionGuard from "auth/PermissionGuard";
import ApplicationGuard from "components/ApplicationGuard";
import { checkPermission } from "services/permission.service";
import { PERMISSIONS } from "auth/permissionCodes";
import { usePermissions } from "hooks/usePermissions";
import LogsPage from "modules/systems/pages/LogsPage";
import ReleaseLogPage from "modules/systems/pages/ReleaseLogPage";
import UserProfile from "modules/systems/pages/UserProfile";
import {
  AccountBalanceWalletOutlined,
  AccountBalanceOutlined,
  AdminPanelSettingsOutlined,
  ApprovalOutlined,
  AssignmentOutlined,
  AssignmentIndOutlined,
  AutoStoriesOutlined,
  BarChartOutlined,
  Biotech,
  BusinessOutlined,
  CalendarMonthOutlined,
  Category,
  DashboardOutlined,
  DescriptionOutlined,
  EngineeringOutlined,
  FactCheckOutlined,
  GroupsOutlined,
  FolderCopyOutlined,
  HistoryOutlined,
  HistoryEdu,
  Input,
  LocationCityOutlined,
  ManageAccountsOutlined,
  NewReleasesOutlined,
  PlaceOutlined,
  Output,
  SettingsBackupRestore,
  VpnKeyOutlined,
  AccountTreeOutlined,
  StraightenOutlined,
  BadgeOutlined,
  SchoolOutlined,
  WorkspacePremiumOutlined
} from "@mui/icons-material";
// Import các component Equipment
import EquipmentCategories from "modules/equipments/components/EquipmentCategories";
import EquipmentBorrow from "modules/equipments/components/EquipmentBorrow";
import EquipmentReturn from "modules/equipments/components/EquipmentReturn";
import EquipmentBorrowHistory from "modules/equipments/components/EquipmentBorrowHistory";
import EquipmentReturnHistory from "modules/equipments/components/EquipmentReturnHistory";
import EquipmentCalibration from "modules/equipments/components/EquipmentCalibration";
// Import AllNotificationsPage
import AllNotificationsPage from "components/AllNotificationsPage";
// Import CalendarPage
import CalendarPage from "modules/contracts/pages/calendars/CalendarPage";
import DocumentTypeManagement from "modules/systems/components/document-types/DocumentTypeManagement";
import CatalogManagement from "modules/systems/components/catalogs/CatalogManagement";
import DepartmentsManagement from "modules/systems/components/catalogs/DepartmentsManagement";
import SubcontractExecutionGroupsManagement from "modules/systems/components/catalogs/SubcontractExecutionGroupsManagement";
// SUBCONTRACT
import {
  DashboardPage as SubcontractDashboardPage,
  SubcontractDetailPage,
  SubcontractListPage,
  SubcontractWorkflowListPage,
  SubcontractWorkflowDetailPage,
  StatisticsPage as SubcontractStatisticsPage,
  DevelopmentPage,
} from "modules/subcontracts";
import ModuleLayout from "components/layouts/ModuleLayout";
import useSubcontractPermissions, {
  SUBCONTRACT_PERMISSIONS,
} from "modules/subcontracts/hooks/useSubcontractPermissions";

const subcontractPermissionFallback = <Navigate to="/unauthorized" replace />;
const subcontractDashboardPermissionFallback = (
  <Box className="subcontract-page">
    <Alert severity="warning">
      Bạn không được quyền xem dashboard quyết định giao khoán.
    </Alert>
  </Box>
);

const SubcontractPermissionGuard = ({ permission, children }) => (
  <PermissionGuard requirePermission={permission} fallback={subcontractPermissionFallback}>
    {children}
  </PermissionGuard>
);

const LEGACY_ROUTES_ENABLED = false;

const NOTIFICATION_READ_PERMISSIONS = [
  PERMISSIONS.CONTRACT.NOTIFICATION_READ,
  PERMISSIONS.SUBCONTRACT.NOTIFICATION_READ,
];

const filterMenuItemsByPathPermission = (items, permissions, permissionByPath, allowAdminBypass = true) =>
  items
    .map((item) => {
      const children = item.children
        ? filterMenuItemsByPathPermission(item.children, permissions, permissionByPath, allowAdminBypass)
        : null;

      if (children) {
        return children.length > 0 ? { ...item, children } : null;
      }

      const permission = permissionByPath[item.path];
      return !permission || (allowAdminBypass && permissions.isAdmin()) || permissions.can(permission) ? item : null;
    })
    .filter(Boolean);

//#region System Module
const systemMenuItems = [
  {
    key: "system-shared-catalogs", label: "Danh mục dùng chung", children: [
      { label: "Loại tài liệu", path: "/systems/document-types", icon: <DescriptionOutlined fontSize="small" /> },
      { label: "Tỉnh/Thành phố", path: "/systems/provinces", icon: <LocationCityOutlined fontSize="small" /> },
      { label: "Phường/Xã", path: "/systems/communes", icon: <PlaceOutlined fontSize="small" /> },
    ]
  },
  {
    key: "system-organization", label: "Cơ cấu tổ chức", children: [
      { label: "Đơn vị", path: "/systems/enterprises", icon: <BusinessOutlined fontSize="small" /> },
      { label: "Phòng ban", path: "/systems/departments", icon: <AccountTreeOutlined fontSize="small" /> },
    ]
  },
  {
    key: "system-personnel-catalogs", label: "Danh mục Nhân sự", children: [
      { label: "Chức vụ", path: "/systems/titles", icon: <BadgeOutlined fontSize="small" /> },
      { label: "Chuyên ngành", path: "/systems/majors", icon: <AutoStoriesOutlined fontSize="small" /> },
      { label: "Trình độ - Bằng cấp", path: "/systems/qualifications", icon: <SchoolOutlined fontSize="small" /> },
      { label: "Loại chứng chỉ", path: "/systems/certificate-types", icon: <WorkspacePremiumOutlined fontSize="small" /> },
    ]
  },
  {
    key: "system-contract-catalogs",
    label: "Danh mục hợp đồng",
    children: [
      { label: "Nhóm hợp đồng", path: "/systems/contract-groups", icon: <FolderCopyOutlined fontSize="small" /> },
      { label: "Đơn vị thi công", path: "/systems/contract-assignees", icon: <EngineeringOutlined fontSize="small" /> },
      { label: "Chủ đầu tư", path: "/systems/clients", icon: <AccountBalanceOutlined fontSize="small" /> },
      { label: "Vai trò nhân sự", path: "/systems/role-in-contract", icon: <AssignmentIndOutlined fontSize="small" /> },
      { label: "Đơn vị tính", path: "/systems/unit-in-contract", icon: <StraightenOutlined fontSize="small" /> },
    ],
  },
  {
    key: "system-subcontract-catalogs",
    label: "Danh mục giao khoán",
    children: [
      { label: "Nhóm thực hiện giao khoán", path: "/systems/subcontract-execution-groups", icon: <GroupsOutlined fontSize="small" /> },
      { label: "Vai trò nhân sự", path: "/systems/role-in-subcontract", icon: <AssignmentIndOutlined fontSize="small" /> },
      { label: "Đơn vị tính", path: "/systems/unit-in-subcontract", icon: <StraightenOutlined fontSize="small" /> },
    ],
  },
  {
    key: "system-hrm",
    label: "Quản lý nhân sự",
    children: [
      { label: "Nhân sự", path: "/systems/personnel", icon: <GroupsOutlined fontSize="small" /> },
      { label: "Người dùng", path: "/systems/users", icon: <ManageAccountsOutlined fontSize="small" /> },
    ],
  },
  {
    key: "system-permissions",
    label: "Phân quyền",
    children: [
      { label: "Phân quyền chức năng", path: "/systems/permissions", icon: <VpnKeyOutlined fontSize="small" /> },
      { label: "Phân quyền ứng dụng", path: "/systems/applications", icon: <AdminPanelSettingsOutlined fontSize="small" /> },
    ],
  },
  {
    key: "system-logs",
    label: "Nhật ký",
    children: [
      { label: "Nhật ký hệ thống", path: "/systems/logs", icon: <HistoryOutlined fontSize="small" /> },
      { label: "Nhật ký phát hành", path: "/systems/release-log", icon: <NewReleasesOutlined fontSize="small" /> },
    ],
  },
];

const systemPageHeaders = {
  "/systems/provinces": { title: "Tỉnh / Thành phố", subtitle: "Quản lý tỉnh / thành phố" },
  "/systems/communes": { title: "Phường / Xã", subtitle: "Quản lý phường / xã" },
  "/systems/enterprises": { title: "Đơn vị", subtitle: "Quản lý đơn vị" },
  "/systems/titles": { title: "Chức vụ", subtitle: "Quản lý chức vụ" },
  "/systems/majors": { title: "Chuyên ngành", subtitle: "Quản lý chuyên ngành" },
  "/systems/qualifications": { title: "Trình độ - Bằng cấp", subtitle: "Quản lý trình độ - bằng cấp" },
  "/systems/certificate-types": { title: "Loại chứng chỉ", subtitle: "Quản lý loại chứng chỉ" },
  // Nhân sự và tài khoản
  "/systems/personnel": { title: "Nhân sự", subtitle: "Quản lý thông tin nhân sự và hồ sơ nhân viên" },
  "/systems/departments": { title: "Phòng ban", subtitle: "Quản lý thông tin phòng ban" },
  "/systems/users": { title: "Người dùng", subtitle: "Quản lý người dùng và quyền truy cập" },
  // Phân quyền hệ thống
  "/systems/permissions": { title: "Phân quyền chức năng", subtitle: "Quản lý phân quyền chức năng của ứng dụng" },
  "/systems/permissions/overview": { title: "Phân quyền chức năng", subtitle: "Tổng quan về phân quyền chức năng" },
  "/systems/permissions/role-permissions": { title: "Phân quyền chức năng", subtitle: "Quản lý phân quyền theo vai trò" },
  "/systems/permissions/user-roles": { title: "Phân quyền chức năng", subtitle: "Quản lý vai trò của người dùng" },
  "/systems/permissions/user-permissions": { title: "Phân quyền chức năng", subtitle: "Quản lý quyền truy cập của người dùng" },
  "/systems/applications": { title: "Phân quyền ứng dụng", subtitle: "Quản lý phân quyền cho các ứng dụng của hệ thống" },
  // Quản lý danh mục
  "/systems/document-types": { title: "Danh mục tài liệu", subtitle: "Quản lý danh mục tài liệu" },
  "/systems/contract-groups": { title: "Nhóm hợp đồng", subtitle: "Quản lý nhóm hợp đồng" },
  "/systems/contract-assignees": { title: "Đơn vị thi công", subtitle: "Quản lý đơn vị thi công" },
  "/systems/clients": { title: "Chủ đầu tư", subtitle: "Quản lý thông tin chủ đầu tư" },
  "/systems/role-in-contract": { title: "Vai trò trong hợp đồng", subtitle: "Quản lý vai trò nhân sự trong hợp đồng" },
  "/systems/unit-in-contract": { title: "Đơn vị trong hợp đồng", subtitle: "Quản lý đơn vị trong hợp đồng" },
  "/systems/subcontract-execution-groups": { title: "Nhóm thực hiện giao khoán", subtitle: "Quản lý thông tin nhóm thực hiện giao khoán" },
  "/systems/role-in-subcontract": { title: "Vai trò trong giao khoán", subtitle: "Quản lý vai trò nhân sự trong giao khoán" },
  "/systems/unit-in-subcontract": { title: "Đơn vị trong giao khoán", subtitle: "Quản lý đơn vị trong giao khoán" },
  // Nhật ký hệ thống
  "/systems/logs": { title: "Nhật ký", subtitle: "Theo dõi hoạt động của hệ thống" },
  "/systems/release-log": { title: "Nhật ký phát hành", subtitle: "Theo dõi các phiên bản phát hành" },
};

const systemPermissionByPath = {
  "/systems/provinces": PERMISSIONS.SYSTEM.PROVINCE_READ,
  "/systems/communes": PERMISSIONS.SYSTEM.COMMUNE_READ,
  "/systems/enterprises": PERMISSIONS.SYSTEM.ENTERPRISE_READ,
  "/systems/titles": PERMISSIONS.SYSTEM.PERSONNEL_READ,
  "/systems/majors": PERMISSIONS.SYSTEM.PERSONNEL_READ,
  "/systems/qualifications": PERMISSIONS.SYSTEM.PERSONNEL_READ,
  "/systems/certificate-types": PERMISSIONS.SYSTEM.PERSONNEL_READ,
  "/systems/document-types": PERMISSIONS.SYSTEM.DOCUMENT_TYPE_READ,
  "/systems/personnel": PERMISSIONS.SYSTEM.PERSONNEL_READ,
  "/systems/departments": PERMISSIONS.SYSTEM.DEPARTMENT_READ,
  "/systems/users": PERMISSIONS.SYSTEM.USER_READ,
  "/systems/contract-groups": PERMISSIONS.SYSTEM.CONTRACT_GROUP_READ,
  "/systems/contract-assignees": PERMISSIONS.SYSTEM.CONTRACTOR_READ,
  "/systems/clients": PERMISSIONS.SYSTEM.CLIENT_READ,
  "/systems/role-in-contract": PERMISSIONS.SYSTEM.ROLE_IN_CONTRACT_READ,
  "/systems/unit-in-contract": PERMISSIONS.SYSTEM.UNIT_IN_CONTRACT_READ,
  "/systems/subcontract-execution-groups": PERMISSIONS.SYSTEM.SUBCONTRACT_EXECUTION_GROUP_READ,
  "/systems/role-in-subcontract": PERMISSIONS.SYSTEM.ROLE_IN_SUBCONTRACT_READ,
  "/systems/unit-in-subcontract": PERMISSIONS.SYSTEM.UNIT_IN_SUBCONTRACT_READ,
  "/systems/permissions": PERMISSIONS.SYSTEM.PERMISSION_FUNCTION_READ,
  "/systems/applications": PERMISSIONS.SYSTEM.PERMISSION_APPLICATION_READ,
  "/systems/logs": PERMISSIONS.SYSTEM.LOG_READ,
  "/systems/release-log": PERMISSIONS.SYSTEM.RELEASE_LOG_READ,
};

const SystemModuleLayout = () => {
  const permissions = usePermissions();
  const menuItems = React.useMemo(
    () => filterMenuItemsByPathPermission(systemMenuItems, permissions, systemPermissionByPath, false),
    [permissions],
  );

  return (
    <ModuleLayout
      menuItems={menuItems}
      pageHeaders={systemPageHeaders}
      defaultHeader={{ title: "Quản trị hệ thống", subtitle: "" }}
      storageKey="system-sidebar-collapsed"
    />
  );
};
// #endregion

//#region Contract Module
function ContractModuleLayout() {
  const { user } = useAuth();
  const [canReadContract, setCanReadContract] = React.useState(false);
  const [canViewCalendar, setCanViewCalendar] = React.useState(false);

  React.useEffect(() => {
    let isMounted = true;

    if (user?.role === "Admin") {
      setCanReadContract(true);
      setCanViewCalendar(true);
      return () => {
        isMounted = false;
      };
    }

    if (!user) {
      setCanReadContract(false);
      setCanViewCalendar(false);
      return () => {
        isMounted = false;
      };
    }

    checkPermission(PERMISSIONS.CONTRACT.CONTRACT_READ).then((result) => {
      if (isMounted) {
        setCanReadContract(result.hasPermission === true);
      }
    });

    checkPermission(PERMISSIONS.CONTRACT.CALENDAR_READ).then((result) => {
      if (isMounted) {
        setCanViewCalendar(result.hasPermission === true);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [user]);

  const contractMenuItems = [
    {
      label: "Tổng quan",
      path: "/contracts/overview",
      icon: <DashboardOutlined fontSize="small" />,
    },
    ...(user?.role === "Admin" || canReadContract
      ? [
        {
          label: "Danh sách hợp đồng",
          path: "/contracts",
          icon: <DescriptionOutlined fontSize="small" />,
        },
      ]
      : []),
    ...(user?.role === "Admin" || canViewCalendar
      ? [
        {
          label: "Lịch biểu",
          path: "/contracts/calendar",
          icon: <CalendarMonthOutlined fontSize="small" />,
        },
      ]
      : []),
  ];

  const contractPageHeaders = {
    "/contracts/overview": { title: "Tổng quan", subtitle: "Tổng quan nhanh tình trạng thực hiện, thời hạn và hồ sơ phê duyệt của hợp đồng" },
    "/contracts": { title: "Danh sách hợp đồng", subtitle: "Theo dõi và quản lý toàn bộ hợp đồng" },
    "/contracts/calendar": { title: "Lịch biểu", subtitle: "Xem lịch biểu các hoạt động liên quan đến hợp đồng" },
  };

  const getContractHeader = (pathname, header) => {
    if (pathname.match(/^\/contracts\/(?!overview$|calendar$|notifications$|items$|history$)[^/]+$/)) {
      return {
        title: "Chi tiết hợp đồng",
        subtitle: "Thông tin, hạng mục và quá trình thực hiện hợp đồng",
      };
    }

    return header;
  };

  return (
    <ModuleLayout
      menuItems={contractMenuItems}
      pageHeaders={contractPageHeaders}
      defaultHeader={{ title: "Quản lý hợp đồng", subtitle: "" }}
      getHeader={getContractHeader}
      storageKey="contract-sidebar-collapsed"
    />
  );
}
// #endregion

//#region Subcontract Module
const subcontractPageHeaders = {
  "/subcontracts/dashboard": {
    title: "Dashboard quyết định giao khoán",
    subtitle: "Tổng quan nhanh tình trạng thực hiện, thời hạn và hồ sơ phê duyệt của giao khoán",
  },
  "/subcontracts/contracts": {
    title: "Danh sách quyết định giao khoán",
    subtitle: "Theo dõi và quản lý toàn bộ quyết định giao khoán",
  },
  "/subcontracts/approvals": {
    title: "Phê duyệt quyết định giao khoán",
    subtitle: "Theo dõi hồ sơ, người gửi và trạng thái phê duyệt gần nhất",
  },
  "/subcontracts/acceptances": {
    title: "Nghiệm thu quyết định giao khoán",
    subtitle: "Theo dõi số kỳ và giá trị nghiệm thu lũy kế của từng quyết định",
  },
  "/subcontracts/acceptance-approvals": {
    title: "Phê duyệt nghiệm thu",
    subtitle: "Theo dõi, gửi và xử lý phê duyệt các kỳ nghiệm thu giao khoán",
  },
  "/subcontracts/payments": {
    title: "Thanh toán quyết định giao khoán",
    subtitle: "Theo dõi số kỳ và tiến độ thanh toán lũy kế của từng quyết định",
  },
  "/subcontracts/statistics": {
    title: "Thống kê quyết định giao khoán",
    subtitle: "Theo dõi số liệu của mỗi quyết định giao khoán và hợp đồng chính liên quan",
  },
};

const getSubcontractHeader = (pathname, header) => {
  if (pathname.match(/^\/subcontracts\/contracts\/[^/]+$/)) {
    return {
      title: "Chi tiết quyết định giao khoán",
      subtitle: "Thông tin, dự toán và quá trình thực hiện quyết định giao khoán",
    };
  }

  if (pathname.match(/^\/subcontracts\/payments\/[^/]+$/)) {
    return {
      title: "Chi tiết thanh toán giao khoán",
      subtitle: "Theo dõi các kỳ và giá trị thanh toán của quyết định giao khoán",
    };
  }

  if (pathname.match(/^\/subcontracts\/acceptances\/[^/]+$/)) {
    return {
      title: "Chi tiết nghiệm thu giao khoán",
      subtitle: "Theo dõi các kỳ và giá trị nghiệm thu của quyết định giao khoán",
    };
  }

  if (pathname.match(/^\/subcontracts\/acceptance-approvals\/[^/]+$/)) {
    return {
      title: "Chi tiết phê duyệt nghiệm thu",
      subtitle: "Theo dõi hồ sơ và trạng thái phê duyệt của các kỳ nghiệm thu giao khoán",
    };
  }

  if (pathname.match(/^\/subcontracts\/approvals\/[^/]+$/)) {
    return {
      title: "Chi tiết phê duyệt giao khoán",
      subtitle: "Theo dõi hồ sơ và trạng thái phê duyệt của quyết định giao khoán",
    };
  }

  return header;
};

function SubcontractModuleLayout() {
  const permissions = useSubcontractPermissions();
  const menuItems = [
    {
      label: "Tổng quan",
      path: "/subcontracts/dashboard",
      icon: <DashboardOutlined fontSize="small" />,
      permission: SUBCONTRACT_PERMISSIONS.dashboardRead,
    },
    {
      label: "Danh sách quyết định giao khoán",
      path: "/subcontracts/contracts",
      icon: <AssignmentOutlined fontSize="small" />,
      permission: SUBCONTRACT_PERMISSIONS.subcontractRead,
    },
    {
      label: "Phê duyệt quyết định giao khoán",
      path: "/subcontracts/approvals",
      icon: <ApprovalOutlined fontSize="small" />,
      permission: SUBCONTRACT_PERMISSIONS.approvalRead,
    },
    {
      label: "Nghiệm thu",
      path: "/subcontracts/acceptances",
      icon: <FactCheckOutlined fontSize="small" />,
      canShow: () => permissions.canReadAcceptance,
    },
    {
      label: "Phê duyệt nghiệm thu",
      path: "/subcontracts/acceptance-approvals",
      icon: <ApprovalOutlined fontSize="small" />,
      permission: SUBCONTRACT_PERMISSIONS.acceptanceApprovalRead,
    },
    {
      label: "Thanh toán",
      path: "/subcontracts/payments",
      icon: <AccountBalanceWalletOutlined fontSize="small" />,
      permission: SUBCONTRACT_PERMISSIONS.paymentRead,
    },
    {
      label: "Thống kê",
      path: "/subcontracts/statistics",
      icon: <BarChartOutlined fontSize="small" />,
      permission: SUBCONTRACT_PERMISSIONS.subcontractRead,
    },
  ].filter((item) => (item.canShow ? item.canShow() : permissions.has(item.permission)));

  return (
    <ModuleLayout
      menuItems={menuItems}
      pageHeaders={subcontractPageHeaders}
      defaultHeader={{ title: "Quản lý quyết định giao khoán", subtitle: "Quản lý thông tin quyết định giao khoán" }}
      getHeader={getSubcontractHeader}
      storageKey="subcontract-sidebar-collapsed"
    />
  );
}
// #endregion

//#region Equipment Module
const equipmentMenuItems = [
  { label: "Danh mục thiết bị", path: "/equipment/categories", icon: <Category fontSize="small" /> },
  { label: "Lập phiếu mượn thiết bị", path: "/equipment/borrow", icon: <Output fontSize="small" /> },
  { label: "Lập phiếu trả thiết bị", path: "/equipment/return", icon: <Input fontSize="small" /> },
  { label: "Lịch sử mượn thiết bị", path: "/equipment/borrow-history", icon: <HistoryEdu fontSize="small" /> },
  { label: "Lịch sử trả thiết bị", path: "/equipment/return-history", icon: <SettingsBackupRestore fontSize="small" /> },
  { label: "Theo dõi hiệu chuẩn", path: "/equipment/calibration", icon: <Biotech fontSize="small" /> },
];

const equipmentPageHeaders = {
  "/equipment/categories": { title: "Danh mục thiết bị", subtitle: "Quản lý danh mục thiết bị" },
  "/equipment/borrow": { title: "Lập phiếu mượn thiết bị", subtitle: "Theo dõi, quản lý phiếu mượn thiết bị" },
  "/equipment/return": { title: "Lập phiếu trả thiết bị", subtitle: "Theo dõi, quản lý phiếu trả thiết bị" },
  "/equipment/borrow-history": { title: "Lịch sử mượn thiết bị", subtitle: "Theo dõi lịch sử mượn thiết bị" },
  "/equipment/return-history": { title: "Lịch sử trả thiết bị", subtitle: "Theo dõi lịch sử trả thiết bị" },
  "/equipment/calibration": { title: "Theo dõi hiệu chuẩn", subtitle: "Theo dõi quá trình hiệu chuẩn thiết bị" },
};

const equipmentPermissionByPath = {
  "/equipment/categories": PERMISSIONS.EQUIPMENT.EQUIPMENT_READ,
  "/equipment/borrow": PERMISSIONS.EQUIPMENT.BORROW_REQUEST_CREATE,
  "/equipment/return": PERMISSIONS.EQUIPMENT.RETURN_REQUEST_CREATE,
  "/equipment/borrow-history": PERMISSIONS.EQUIPMENT.BORROW_HISTORY_READ,
  "/equipment/return-history": PERMISSIONS.EQUIPMENT.RETURN_HISTORY_READ,
  "/equipment/calibration": PERMISSIONS.EQUIPMENT.CALIBRATION_READ,
};

const EquipmentModuleLayout = () => {
  const permissions = usePermissions();
  const menuItems = React.useMemo(
    () => filterMenuItemsByPathPermission(equipmentMenuItems, permissions, equipmentPermissionByPath),
    [permissions],
  );

  return (
    <ModuleLayout
      menuItems={menuItems}
      pageHeaders={equipmentPageHeaders}
      defaultHeader={{ title: "Quản lý thiết bị", subtitle: "Quản lý thông tin thiết bị" }}
      storageKey="equipment-sidebar-collapsed"
    />
  );
};
// #endregion

const SystemDevelopmentPage = ({ title }) => <DevelopmentPage title={title} />;

function HomeRedirect() {
  const { isAuthenticated } = useAuth();
  if (isAuthenticated) {
    return <Navigate to="/portal" replace />;
  } else {
    return <Navigate to="/login" replace />;
  }
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AuthorizationProvider>
          <PageTitleProvider>
            <ConfirmProvider>
              <Routes>
                {/* CORE: điều hướng route, đăng nhập, đăng ký */}
                <Route path="/" element={<HomeRedirect />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />

                {/* PORTAL: màn hình chọn ứng dụng sau đăng nhập */}
                <Route
                  path="/portal"
                  element={
                    <ProtectedRoute>
                      <PortalPage />
                    </ProtectedRoute>
                  }
                />

                {/* SYSTEM MODULE: nhân sự, người dùng, phân quyền, danh mục, nhật ký */}
                <Route
                  path="/systems"
                  element={
                    <ProtectedRoute>
                      <SystemModuleLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="document-types" element={<PermissionGuard requirePermission={PERMISSIONS.SYSTEM.DOCUMENT_TYPE_READ}><DocumentTypeManagement /></PermissionGuard>} />
                  <Route path="provinces" element={<ScopedPermissionGuard permissions={[PERMISSIONS.APPLICATION.SYSTEM_ACCESS, PERMISSIONS.SYSTEM.PROVINCE_READ]}><CatalogManagement key="provinces" type="provinces" pageTitle="Tỉnh / Thành phố" /></ScopedPermissionGuard>} />
                  <Route path="communes" element={<ScopedPermissionGuard permissions={[PERMISSIONS.APPLICATION.SYSTEM_ACCESS, PERMISSIONS.SYSTEM.COMMUNE_READ]}><CatalogManagement key="communes" type="communes" pageTitle="Phường / Xã" /></ScopedPermissionGuard>} />
                  <Route path="enterprises" element={<ScopedPermissionGuard permissions={[PERMISSIONS.APPLICATION.SYSTEM_ACCESS, PERMISSIONS.SYSTEM.ENTERPRISE_READ]}><CatalogManagement key="enterprises" type="enterprises" pageTitle="Đơn vị" /></ScopedPermissionGuard>} />
                  <Route path="titles" element={<ScopedPermissionGuard permissions={[PERMISSIONS.APPLICATION.SYSTEM_ACCESS, PERMISSIONS.SYSTEM.PERSONNEL_READ]}><CatalogManagement key="titles" type="titles" pageTitle="Chức vụ" /></ScopedPermissionGuard>} />
                  <Route path="majors" element={<ScopedPermissionGuard permissions={[PERMISSIONS.APPLICATION.SYSTEM_ACCESS, PERMISSIONS.SYSTEM.PERSONNEL_READ]}><CatalogManagement key="majors" type="majors" pageTitle="Chuyên ngành" /></ScopedPermissionGuard>} />
                  <Route path="qualifications" element={<ScopedPermissionGuard permissions={[PERMISSIONS.APPLICATION.SYSTEM_ACCESS, PERMISSIONS.SYSTEM.PERSONNEL_READ]}><CatalogManagement key="qualifications" type="qualifications" pageTitle="Trình độ - Bằng cấp" /></ScopedPermissionGuard>} />
                  <Route path="certificate-types" element={<ScopedPermissionGuard permissions={[PERMISSIONS.APPLICATION.SYSTEM_ACCESS, PERMISSIONS.SYSTEM.PERSONNEL_READ]}><CatalogManagement key="certificateTypes" type="certificateTypes" pageTitle="Loại chứng chỉ" /></ScopedPermissionGuard>} />
                  {/* Quản lý nhân sự */}
                  <Route index element={<Navigate to="personnel" replace />} />
                  <Route
                    path="personnel"
                    element={
                      <PermissionGuard requirePermission={PERMISSIONS.SYSTEM.PERSONNEL_READ}>
                        <PersonnelList />
                      </PermissionGuard>
                    }
                  />
                  <Route path="departments" element={<ScopedPermissionGuard permissions={[PERMISSIONS.APPLICATION.SYSTEM_ACCESS, PERMISSIONS.SYSTEM.DEPARTMENT_READ]}><DepartmentsManagement /></ScopedPermissionGuard>} />
                  <Route
                    path="users"
                    element={
                      <PermissionGuard requirePermission={PERMISSIONS.SYSTEM.USER_READ}>
                        <UserList />
                      </PermissionGuard>
                    }
                  />
                  {/* Danh mục hợp đồng */}
                  <Route path="contract-groups" element={<PermissionGuard requirePermission={PERMISSIONS.SYSTEM.CONTRACT_GROUP_READ}><CatalogManagement type="contractGroups" pageTitle="Nhóm hợp đồng" /></PermissionGuard>} />
                  <Route path="contract-assignees" element={<PermissionGuard requirePermission={PERMISSIONS.SYSTEM.CONTRACTOR_READ}><CatalogManagement type="contractors" pageTitle="Đơn vị thi công" /></PermissionGuard>} />
                  <Route path="clients" element={<PermissionGuard requirePermission={PERMISSIONS.SYSTEM.CLIENT_READ}><CatalogManagement type="clients" pageTitle="Chủ đầu tư" /></PermissionGuard>} />
                  <Route path="role-in-contract" element={<PermissionGuard requirePermission={PERMISSIONS.SYSTEM.ROLE_IN_CONTRACT_READ}><CatalogManagement type="roleInContracts" pageTitle="Vai trò trong hợp đồng" /></PermissionGuard>} />
                  <Route path="role-in-contracts" element={<PermissionGuard requirePermission={PERMISSIONS.SYSTEM.ROLE_IN_CONTRACT_READ}><CatalogManagement type="roleInContracts" pageTitle="Vai trò trong hợp đồng" /></PermissionGuard>} />
                  <Route path="unit-in-contract" element={<PermissionGuard requirePermission={PERMISSIONS.SYSTEM.UNIT_IN_CONTRACT_READ}><CatalogManagement type="unitInContracts" pageTitle="Đơn vị trong hợp đồng" /></PermissionGuard>} />
                  <Route path="unit-in-contracts" element={<PermissionGuard requirePermission={PERMISSIONS.SYSTEM.UNIT_IN_CONTRACT_READ}><CatalogManagement type="unitInContracts" pageTitle="Đơn vị trong hợp đồng" /></PermissionGuard>} />
                  {/* Danh mục giao khoán */}
                  <Route path="subcontract-execution-groups" element={<PermissionGuard requirePermission={PERMISSIONS.SYSTEM.SUBCONTRACT_EXECUTION_GROUP_READ}><SubcontractExecutionGroupsManagement /></PermissionGuard>} />
                  <Route path="role-in-subcontract" element={<PermissionGuard requirePermission={PERMISSIONS.SYSTEM.ROLE_IN_SUBCONTRACT_READ}><CatalogManagement type="roleInSubcontracts" pageTitle="Vai trò trong giao khoán" /></PermissionGuard>} />
                  <Route path="role-in-subcontracts" element={<PermissionGuard requirePermission={PERMISSIONS.SYSTEM.ROLE_IN_SUBCONTRACT_READ}><CatalogManagement type="roleInSubcontracts" pageTitle="Vai trò trong giao khoán" /></PermissionGuard>} />
                  <Route path="unit-in-subcontract" element={<PermissionGuard requirePermission={PERMISSIONS.SYSTEM.UNIT_IN_SUBCONTRACT_READ}><CatalogManagement type="unitInSubcontracts" pageTitle="Đơn vị trong giao khoán" /></PermissionGuard>} />
                  <Route path="unit-in-subcontracts" element={<PermissionGuard requirePermission={PERMISSIONS.SYSTEM.UNIT_IN_SUBCONTRACT_READ}><CatalogManagement type="unitInSubcontracts" pageTitle="Đơn vị trong giao khoán" /></PermissionGuard>} />
                  {/* Phân quyền */}
                  <Route
                    path="permissions"
                    element={
                      <PermissionGuard requirePermission={PERMISSIONS.SYSTEM.PERMISSION_FUNCTION_READ}>
                        <PermissionManagement />
                      </PermissionGuard>
                    }
                  >
                    <Route index element={<Navigate to="overview" replace />} />
                    <Route path="overview" element={<PermissionOverview />} />
                    <Route path="role-permissions" element={<RolePermissionManagement />} />
                    <Route path="user-roles" element={<UserRoleManagement />} />
                    <Route path="user-permissions" element={<UserPermissionManagement />} />
                  </Route>
                  <Route
                    path="applications/*"
                    element={
                      <PermissionGuard requirePermission={PERMISSIONS.SYSTEM.PERMISSION_APPLICATION_READ}>
                        <ApplicationManagement />
                      </PermissionGuard>
                    }
                  />
                  {/* Nhật ký */}
                  <Route
                    path="logs"
                    element={
                      <PermissionGuard requirePermission={PERMISSIONS.SYSTEM.LOG_READ}>
                        <LogsPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="release-log"
                    element={
                      <PermissionGuard requirePermission={PERMISSIONS.SYSTEM.RELEASE_LOG_READ}>
                        <ReleaseLogPage />
                      </PermissionGuard>
                    }
                  />
                </Route>

                {/* CONTRACT MODULE: điều hướng từ /dashboard về module hợp đồng */}
                <Route path="/dashboard/*" element={<Navigate to="/contracts/overview" replace />} />
                {/* CONTRACT MODULE: quản lý hợp đồng chính */}
                <Route
                  path="/contracts"
                  element={
                    <ProtectedRoute>
                      <ApplicationGuard requireApplication="contract">
                        <ContractModuleLayout />
                      </ApplicationGuard>
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<PermissionGuard requirePermission={PERMISSIONS.CONTRACT.CONTRACT_READ}><ProjectList /></PermissionGuard>} />
                  <Route path="overview" element={<SystemDevelopmentPage title="Tổng quan" />} />
                  <Route path=":contractId" element={<PermissionGuard requirePermission={PERMISSIONS.CONTRACT.CONTRACT_READ}><ContractDetail /></PermissionGuard>} />
                  <Route path="items" element={<SystemDevelopmentPage title="Hàng mục hợp đồng" />} />
                  <Route path="history" element={<SystemDevelopmentPage title="Lịch sử hợp đồng" />} />
                  <Route path="calendar" element={<PermissionGuard requirePermission={PERMISSIONS.CONTRACT.CALENDAR_READ}><CalendarPage /></PermissionGuard>} />
                  <Route path="notifications" element={<Navigate to="/notifications" replace />} />
                </Route>

                {/* SUBCONTRACT MODULE: quyết định giao khoán */}
                <Route
                  path="/subcontracts"
                  element={
                    <ProtectedRoute>
                      <ApplicationGuard requireApplication="subcontract">
                        <SubcontractModuleLayout />
                      </ApplicationGuard>
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="dashboard" replace />} />
                  <Route
                    path="dashboard"
                    element={(
                      <PermissionGuard
                        requirePermission={PERMISSIONS.SUBCONTRACT.DASHBOARD_READ}
                        fallback={subcontractDashboardPermissionFallback}
                      >
                        <SubcontractDashboardPage />
                      </PermissionGuard>
                    )}
                  />
                  <Route path="contracts" element={<SubcontractPermissionGuard permission={PERMISSIONS.SUBCONTRACT.SUBCONTRACT_READ}><SubcontractListPage /></SubcontractPermissionGuard>} />
                  <Route path="contracts/:id" element={<SubcontractPermissionGuard permission={PERMISSIONS.SUBCONTRACT.SUBCONTRACT_READ}><SubcontractDetailPage /></SubcontractPermissionGuard>} />
                  <Route path="acceptances" element={<SubcontractPermissionGuard permission={PERMISSIONS.SUBCONTRACT.SUBCONTRACT_ACCEPTANCE_READ}><SubcontractWorkflowListPage type="acceptances" /></SubcontractPermissionGuard>} />
                  <Route path="acceptances/:id" element={<SubcontractPermissionGuard permission={PERMISSIONS.SUBCONTRACT.SUBCONTRACT_ACCEPTANCE_READ}><SubcontractWorkflowDetailPage type="acceptances" /></SubcontractPermissionGuard>} />
                  <Route path="acceptance-approvals" element={<SubcontractPermissionGuard permission={PERMISSIONS.SUBCONTRACT.ACCEPTANCE_APPROVAL_READ}><SubcontractWorkflowListPage type="acceptance-approvals" /></SubcontractPermissionGuard>} />
                  <Route path="acceptance-approvals/:id" element={<SubcontractPermissionGuard permission={PERMISSIONS.SUBCONTRACT.ACCEPTANCE_APPROVAL_READ}><SubcontractWorkflowDetailPage type="acceptance-approvals" /></SubcontractPermissionGuard>} />
                  <Route path="approvals" element={<SubcontractPermissionGuard permission={PERMISSIONS.SUBCONTRACT.SUBCONTRACT_APPROVAL_READ}><SubcontractWorkflowListPage type="approvals" /></SubcontractPermissionGuard>} />
                  <Route path="approvals/:id" element={<SubcontractPermissionGuard permission={PERMISSIONS.SUBCONTRACT.SUBCONTRACT_APPROVAL_READ}><SubcontractWorkflowDetailPage type="approvals" /></SubcontractPermissionGuard>} />
                  <Route path="payments" element={<SubcontractPermissionGuard permission={PERMISSIONS.SUBCONTRACT.SUBCONTRACT_PAYMENT_READ}><SubcontractWorkflowListPage type="payments" /></SubcontractPermissionGuard>} />
                  <Route path="payments/:id" element={<SubcontractPermissionGuard permission={PERMISSIONS.SUBCONTRACT.SUBCONTRACT_PAYMENT_READ}><SubcontractWorkflowDetailPage type="payments" /></SubcontractPermissionGuard>} />
                  <Route path="statistics" element={<SubcontractPermissionGuard permission={PERMISSIONS.SUBCONTRACT.STATISTICS_READ}><SubcontractStatisticsPage /></SubcontractPermissionGuard>} />
                </Route>

                {/* EQUIPMENT MODULE: quản lý thiết bị */}
                <Route
                  path="/equipment"
                  element={
                    <ProtectedRoute>
                      <EquipmentModuleLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="categories" replace />} />
                  <Route path="categories" element={<PermissionGuard requirePermission={PERMISSIONS.EQUIPMENT.EQUIPMENT_READ}><EquipmentCategories /></PermissionGuard>} />
                  <Route path="borrow" element={<PermissionGuard requirePermission={PERMISSIONS.EQUIPMENT.BORROW_REQUEST_CREATE}><EquipmentBorrow /></PermissionGuard>} />
                  <Route path="return" element={<PermissionGuard requirePermission={PERMISSIONS.EQUIPMENT.RETURN_REQUEST_CREATE}><EquipmentReturn /></PermissionGuard>} />
                  <Route path="borrow-history" element={<PermissionGuard requirePermission={PERMISSIONS.EQUIPMENT.BORROW_HISTORY_READ}><EquipmentBorrowHistory /></PermissionGuard>} />
                  <Route path="return-history" element={<PermissionGuard requirePermission={PERMISSIONS.EQUIPMENT.RETURN_HISTORY_READ}><EquipmentReturnHistory /></PermissionGuard>} />
                  <Route path="calibration" element={<PermissionGuard requirePermission={PERMISSIONS.EQUIPMENT.CALIBRATION_READ}><EquipmentCalibration /></PermissionGuard>} />
                </Route>

                {/* APPLICATION DEVELOPMENT: trang ứng dụng đang phát triển */}
                <Route
                  path="/applications/:applicationId"
                  element={
                    <ProtectedRoute>
                      <ApplicationDevelopmentPage />
                    </ProtectedRoute>
                  }
                />

                {/* COMMON: trang báo lỗi và hồ sơ cá nhân */}
                <Route path="/unauthorized" element={<Unauthorized />} />
                <Route
                  path="/notifications"
                  element={
                    <ProtectedRoute>
                      <PermissionGuard requireAnyPermissions={NOTIFICATION_READ_PERMISSIONS}>
                        <ModuleLayout
                          menuItems={[]}
                          pageHeaders={{
                            "/notifications": {
                              title: "Trung tâm thông báo",
                              subtitle: "Theo dõi tất cả thông báo của bạn",
                            },
                          }}
                          defaultHeader={{ title: "Trung tâm thông báo", subtitle: "" }}
                          storageKey="notifications-sidebar-collapsed"
                        />
                      </PermissionGuard>
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<AllNotificationsPage />} />
                </Route>
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <ModuleLayout
                        menuItems={[]}
                        pageHeaders={{
                          "/profile": {
                            title: "Thông tin cá nhân",
                            subtitle: "Quản lý thông tin và bảo mật tài khoản",
                          },
                        }}
                        defaultHeader={{ title: "Thông tin cá nhân", subtitle: "" }}
                        storageKey="profile-sidebar-collapsed"
                      />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<UserProfile />} />
                </Route>

                {/* LEGACY ADMIN ROUTES: các route admin cũ giử để tương thích ngược */}
                {/* LEGACY ROUTES: các route cũ dùng AppLayout được giữ lại để tham chiếu, không đăng ký vào router mới. */}
                {LEGACY_ROUTES_ENABLED && (
                  <>
                    <Route
                      path="/admin/users"
                      element={
                        <ProtectedRoute>
                          <PermissionGuard requirePermission={PERMISSIONS.SYSTEM.USER_READ}>
                            <AppLayout>
                              <UserList />
                            </AppLayout>
                          </PermissionGuard>
                        </ProtectedRoute>
                      }
                    />

                    {/* Trang cha quản lý phân quyền */}
                    <Route
                      path="/admin/permissions"
                      element={
                        <ProtectedRoute>
                          <PermissionGuard requirePermission={PERMISSIONS.SYSTEM.PERMISSION_FUNCTION_READ}>
                            <AppLayout>
                              <PermissionManagement />
                            </AppLayout>
                          </PermissionGuard>
                        </ProtectedRoute>
                      }
                    >
                      {/* Nested routes cho các tabs */}
                      <Route index element={<Navigate to="overview" replace />} />
                      <Route path="overview" element={<PermissionOverview />} />
                      <Route
                        path="role-permissions"
                        element={<RolePermissionManagement />}
                      />
                      <Route path="user-roles" element={<UserRoleManagement />} />
                      <Route
                        path="user-permissions"
                        element={<UserPermissionManagement />}
                      />
                    </Route>
                    {/* Giữ lại các route cũ để tương thích ngược */}
                    <Route
                      path="/admin/role-permissions"
                      element={
                        <Navigate to="/admin/permissions/role-permissions" replace />
                      }
                    />
                    <Route
                      path="/admin/user-roles"
                      element={<Navigate to="/admin/permissions/user-roles" replace />}
                    />
                    <Route
                      path="/admin/applications/*"
                      element={
                        <ProtectedRoute>
                          <PermissionGuard requirePermission={PERMISSIONS.SYSTEM.PERMISSION_APPLICATION_READ}>
                            <AppLayout>
                              <ApplicationManagement />
                            </AppLayout>
                          </PermissionGuard>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/user-permission-management"
                      element={
                        <Navigate to="/admin/permissions/user-permissions" replace />
                      }
                    />
                    <Route
                      path="/admin/categories"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <DocumentTypeManagement />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    {/* LEGACY CUSTOMER ROUTES: chủ đầu tư */}
                    <Route
                      path="/customers/info"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <div>Thông tin chủ đầu tư</div>
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/customers/contracts"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <div>Dự án của chủ đầu tư</div>
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    {/* LEGACY STAFF ROUTES: nhân sự */}
                    <Route
                      path="/staff/info"
                      element={
                        <ProtectedRoute>
                          <PermissionGuard requirePermission={PERMISSIONS.SYSTEM.PERSONNEL_READ}>
                            <AppLayout>
                              <PersonnelList />
                            </AppLayout>
                          </PermissionGuard>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/staff/departments"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <div>Phòng ban</div>
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    {/* LEGACY ACCOUNTING ROUTES: kế toán */}
                    <Route
                      path="/accounting/payments"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <div>Quản lý thanh toán</div>
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />{" "}
                    {/* LEGACY ADMIN LOG ROUTES: nhật ký hệ thống */}
                    {/* Removed duplicate /admin/categories route */}
                    <Route
                      path="/admin/logs"
                      element={
                        <ProtectedRoute>
                          <PermissionGuard requirePermission={PERMISSIONS.SYSTEM.LOG_READ}>
                            <AppLayout>
                              <LogsPage />
                            </AppLayout>
                          </PermissionGuard>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/release-log"
                      element={
                        <ProtectedRoute>
                          <PermissionGuard requirePermission={PERMISSIONS.SYSTEM.RELEASE_LOG_READ}>
                            <AppLayout>
                              <ReleaseLogPage />
                            </AppLayout>
                          </PermissionGuard>
                        </ProtectedRoute>
                      }
                    />
                  </>
                )}
              </Routes>
            </ConfirmProvider>
          </PageTitleProvider>
        </AuthorizationProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
